<template>
	<section class="vcx-section section--podcast">
		<div class="vcx-container">
			<div class="vcx-grid">
				<div class="vcx-col-12 vcx-col-6-lg vcx-col-offset-3-lg vcx-text-center-lg">
					<span class="vcx-label-eyebrow">Onward Podcast</span>
					<h2 class="vcx-h1 vcx-mb-100">Insights</h2>

					<p class="vcx-body-md vcx-body-lg-responsive vcx-text-gray-40">
						Onward is a Fundrise podcast offering unique insights and perspectives from company co-founder
						and CEO Ben Miller and guests.
					</p>
				</div>
			</div>

			<div class="vcx-grid vcx-mt-300">
				<div class="vcx-col-12 vcx-col-10-xl vcx-col-offset-1-xl">
					<article class="vcx-featured-podcast">
						<div class="vcx-featured-podcast__content">
							<Badge icon-name="discover" text="New" background="slate" />
							<h3 class="vcx-h4 vcx-my-100"
								>In case you missed it: Watch the first Innovation Fund Investor Day</h3
							>
							<p class="vcx-body-md">
								Watch the full recording as our leadership team lays out the vision for our Public
								Venture Capital (PVC) fund.
							</p>
						</div>
						<div class="vcx-featured-podcast__video">
							<iframe
								src="https://www.youtube.com/embed/d8WL_qbqpT8"
								title="Fundrise Innovation Fund (VCX) | Investor Day 2026"
								frameborder="0"
								allow="autoplay; encrypted-media; picture-in-picture; web-share"
								referrerpolicy="strict-origin-when-cross-origin"
								allowfullscreen
								loading="lazy"
							></iframe>
						</div>
					</article>
				</div>
			</div>

			<div v-if="showFeaturedPodcasts" class="vcx-grid vcx-mt-300 vcx-mt-600-lg">
				<div class="vcx-col-12 vcx-col-10-xl vcx-col-offset-1-xl vcx-col-8-xxl vcx-col-offset-2-xxl">
					<div
						class="vcx-podcasts vcx-grid vcx-gap-150 vcx-gap-300-lg vcx-pl-50 vcx-pr-50 vcx-pl-0-lg vcx-pr-0-lg"
					>
						<article
							v-for="podcast in props.podcasts"
							:key="podcast.title"
							class="podcast-article-card vcx-col-12 vcx-col-6-lg"
						>
							<figure class="podcast-card">
								<img
									v-if="podcast.podcastImage?.asset"
									:src="getPodcastImageUrl(podcast)"
									:alt="`Cover image for ${podcast.title}`"
									loading="lazy"
									decoding="async"
									class="podcast-card-cover"
								/>
								<figcaption class="podcast-card-caption">
									<h3 class="vcx-body-lg-bold">{{ podcast.title }}</h3>
									<p
										v-if="podcast.description"
										class="vcx-body-md vcx-text-gray-30 vcx-mt-0 vcx-mb-100"
										>{{ podcast.description }}</p
									>
									<div class="podcast-card-links">
										<a
											v-if="podcast.applePodcastLink"
											:href="podcast.applePodcastLink"
											target="_blank"
											rel="noopener noreferrer"
											class="vcx-no-wrap"
											@click="trackPodcastClick(podcast.title, podcast.applePodcastLink)"
										>
											<BaseSvgIcon name="video-play" /> Listen
											<span class="show-for-sr">to {{ podcast.title }}</span
											>on Apple
										</a>
										<a
											v-if="podcast.spotifyPodcastLink"
											:href="podcast.spotifyPodcastLink"
											target="_blank"
											rel="noopener noreferrer"
											class="vcx-no-wrap"
											@click="trackPodcastClick(podcast.title, podcast.spotifyPodcastLink)"
										>
											<BaseSvgIcon name="video-play" /> Listen
											<span class="show-for-sr">to {{ podcast.title }}</span> on Spotify
										</a>
									</div>
								</figcaption>
							</figure>
							<div class="podcast-card-links links--mobile">
								<a
									v-if="podcast.applePodcastLink"
									:href="podcast.applePodcastLink"
									target="_blank"
									rel="noopener noreferrer"
									class="vcx-no-wrap"
									@click="trackPodcastClick(podcast.title, podcast.applePodcastLink)"
								>
									<BaseSvgIcon name="video-play" /> Listen
									<span class="show-for-sr">to {{ podcast.title }}</span
									>on Apple
								</a>
								<a
									v-if="podcast.spotifyPodcastLink"
									:href="podcast.spotifyPodcastLink"
									target="_blank"
									rel="noopener noreferrer"
									class="vcx-no-wrap"
									@click="trackPodcastClick(podcast.title, podcast.spotifyPodcastLink)"
								>
									<BaseSvgIcon name="video-play" /> Listen
									<span class="show-for-sr">to {{ podcast.title }}</span> on Spotify
								</a>
							</div>
						</article>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<script setup lang="ts">
import Badge from '@components/badges/badge-component.vue';
import { computed } from 'vue';
import { SanityFeaturedPodcast } from 'types/sanity/vcx-landing-page';
import { useMixpanel } from '@utils/composables/use-mixpanel';
import { useSanityImage } from '@utils/composables/use-sanity-image';

interface Props {
	podcasts?: Array<SanityFeaturedPodcast>;
}

const props = withDefaults(defineProps<Props>(), {
	podcasts: () => []
});

const showFeaturedPodcasts = computed(() => props.podcasts && props.podcasts.length > 0);

const { trackMixpanelClick } = useMixpanel();

function getPodcastImageUrl(podcast: SanityFeaturedPodcast): string | undefined {
	return useSanityImage(podcast.podcastImage?.asset?.url, 'thumbnail');
}

function trackPodcastClick(podcastTitle: string, podcastUrl: string) {
	trackMixpanelClick(
		{
			'Action Element': 'Podcast Link',
			'Action Target': podcastTitle,
			'Action Context': 'VCX',
			Link: podcastUrl
		},
		true
	);
}
</script>

<style scoped lang="scss">
@use '../../styles/vcx/responsive' as *;

.vcx-featured-podcast {
	display: flex;
	border-radius: var(--vcx-radius-100);
	border: 1px solid var(--vcx-gray-70);
	padding: var(--vcx-space-150);
	gap: var(--vcx-space-100);

	@include vcx-media-smo {
		flex-direction: column;
	}

	@include vcx-media-md {
		align-items: center;

		&__content,
		&__video {
			width: 50%;
		}
	}

	&__video iframe {
		border-radius: var(--vcx-radius-100);
		width: 100%;
		aspect-ratio: 16 / 9;
	}
}

.podcast-article-card {
	border-bottom: 1px solid var(--vcx-gray-70);
	padding-bottom: var(--vcx-space-150);

	@include vcx-media-lg {
		border-bottom: none;
		padding-bottom: 0;
	}

	.podcast-card-links {
		display: none;
		flex-direction: row;
		gap: var(--vcx-space-100);

		@include vcx-media-lg {
			display: flex;
		}

		&.links--mobile {
			display: inline-flex;
			width: 100%;
			@include vcx-media-lg {
				display: none;
			}
		}

		a {
			display: inline-flex;
			align-items: center;
			gap: var(--vcx-space-25);
			font-size: 16px;
			color: var(--vcx-gray-40);
			text-decoration: none;
			transition: all var(--vcx-transition-fast);

			:deep(svg) {
				color: var(--vcx-brand-secondary);
				transition: color var(--vcx-transition-fast);
			}

			&:hover {
				color: var(--vcx-gray-white);

				:deep(svg) {
					color: var(--vcx-brand-primary);
				}
			}
		}
	}
}

.podcast-card {
	display: flex;
	flex-direction: row-reverse;
	justify-content: space-between;
	gap: var(--vcx-space-150);
	margin: 0;
	text-align: left;

	@include vcx-media-lg {
		flex-direction: row;
		justify-content: flex-start;
		border-bottom: none;
		padding-bottom: 0;
	}

	.podcast-card-cover {
		height: 75px;
		max-width: 75px;
		flex-shrink: 0;

		@include vcx-media-lg {
			height: 100px;
			max-width: 100px;
		}
	}

	.podcast-card-caption {
		display: flex;
		flex-direction: column;
	}
}
</style>
